import { useCallback } from 'react';
import { useTranslation, LOCALE_IDS } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from '../../lib/brandText';

// ─────────────────────────────────────────────────────────────────────────────
// Strings for the FOREIGN onboarding form (PM-0061) — the `auth.onboarding.
// foreign.*` tree.
//
// They belong in the kit's auth.json and move there with the next kit release
// (the kit-lag rule, exactly as src/screens/MyPage/myPageStrings.js): until
// then they live here, and `useOnboardingStrings` asks the kit FIRST, so the
// day a released catalogue has a key the kit's wording wins and this entry is
// dead. Persian is authoritative; en, ar, fr and de carry every key (parity is tested).
// ─────────────────────────────────────────────────────────────────────────────

import fa from './strings/fa.json';
import en from './strings/en.json';
import ar from './strings/ar.json';
import fr from './strings/fr.json';
import de from './strings/de.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar), fr: flatten(fr), de: flatten(de) };

const owns = (key) => Object.prototype.hasOwnProperty.call(STRINGS.fa, key);

/**
 * Look a key up in the local table (English, then Persian, as the fallback).
 * The brand's overlay and tokens apply as in the kit's catalogues
 * (src/lib/brandText.js).
 */
export function localOnboardingString(locale, key) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(value, undefined, from);
}

/** `t` for this screen: the kit catalogue when it has the key, the local table otherwise. */
export function useOnboardingStrings() {
  const { t, has, locale } = useTranslation();
  return useCallback(
    (key, vars) => (has(key) || !owns(key) ? t(key, vars) : localOnboardingString(locale, key)),
    [t, has, locale],
  );
}

// Every language the kit ships (a copy: a test may sort it), so a missing table
// here is a test failure rather than a quiet fallback.
export const ONBOARDING_STRING_LOCALES = [...LOCALE_IDS];
export const ONBOARDING_STRING_KEYS = Object.keys(STRINGS.fa);
export const onboardingStringTable = (locale) => STRINGS[locale];
